import { useLocation, Link } from 'react-router-dom'
import { Bell, LogOut, ArrowLeft } from 'lucide-react'
import { useAuth } from '../../contexts/useAuth'

/** Detect if the current route is an event review route */
function useEventReviewMode(): {
  isEventReview: boolean
  fundId?: string
  eventId?: string
} {
  const location = useLocation()
  const match = location.pathname.match(/^\/funds\/([^/]+)\/events\/([^/]+)$/)
  if (match) {
    return { isEventReview: true, fundId: match[1], eventId: match[2] }
  }
  return { isEventReview: false }
}

/** Derive a page title from the current path */
function usePageTitle(): string {
  const location = useLocation()
  const path = location.pathname
  if (path === '/dashboard') return 'Dashboard'
  if (path === '/upload') return 'Upload'
  if (path.startsWith('/funds')) return 'Funds'
  if (path === '/admin/users') return 'User Management'
  if (path === '/admin/eqs') return 'EQS Admin'
  if (path === '/admin/policy') return 'Policy Configuration'
  if (path === '/admin/health') return 'System Health'
  return 'NOLOS'
}

export default function TopBar() {
  const { user, logout } = useAuth()
  const { isEventReview, fundId } = useEventReviewMode()
  const pageTitle = usePageTitle()

  if (isEventReview) {
    return (
      <header className="h-14 flex items-center justify-between px-6 border-b border-topbar-border bg-white">
        {/* Left: back breadcrumb */}
        <Link
          to={`/funds/${fundId}`}
          className="flex items-center gap-2 text-sm text-nolos-primary hover:underline"
        >
          <ArrowLeft size={16} />
          <span>Back to Fund</span>
        </Link>

        {/* Center: progress dots placeholder */}
        <div className="flex items-center gap-1.5">
          {[1, 2, 3, 4, 5].map((step) => (
            <div key={step} className="w-2 h-2 rounded-full bg-slate-300" />
          ))}
        </div>

        {/* Right: exit + bell + avatar */}
        <div className="flex items-center gap-4">
          <Link to={`/funds/${fundId}`} className="text-sm text-slate-600 hover:text-slate-900">
            Exit review
          </Link>
          <button className="text-slate-500 hover:text-slate-700">
            <Bell size={18} />
          </button>
          <div className="w-8 h-8 rounded-full bg-nolos-primary text-white flex items-center justify-center text-xs font-bold">
            {user?.name?.charAt(0).toUpperCase() ?? '?'}
          </div>
        </div>
      </header>
    )
  }

  return (
    <header className="h-14 flex items-center justify-between px-6 border-b border-topbar-border bg-white">
      {/* Left: page title */}
      <h1 className="text-lg font-semibold text-slate-900">{pageTitle}</h1>

      {/* Right: user context */}
      <div className="flex items-center gap-4">
        {user && (
          <>
            <span className="text-sm text-slate-600">{user.name}</span>
            <span className="text-xs px-2 py-0.5 rounded-full bg-slate-100 text-slate-600 capitalize">
              {user.role.replace('_', ' ')}
            </span>
          </>
        )}
        <button className="text-slate-500 hover:text-slate-700">
          <Bell size={18} />
        </button>
        <div className="w-8 h-8 rounded-full bg-nolos-primary text-white flex items-center justify-center text-xs font-bold">
          {user?.name?.charAt(0).toUpperCase() ?? '?'}
        </div>
        <button onClick={logout} className="text-slate-500 hover:text-slate-700" title="Logout">
          <LogOut size={18} />
        </button>
      </div>
    </header>
  )
}
